import React, { useState } from "react";

/* Form */
import { Controller, useForm } from "react-hook-form";

/* Material UI */
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

/* Style */
import { useStyles } from "../styles";

/* Hooks */
import { useEnqueueSnackbar } from "../../../../hooks/useEnqueueSnackbar";
import { useSettingDispatchChange } from "../index";
import { VALET_TICKETLESS_SEND_MODE } from "../../../../constants";

const ValetAreaTicketlessSettingsForm = ({ entityID, settingValues }) => {
  const classes = useStyles();
  const [areSettingsExpanded, setAreSettingsExpanded] = useState(true);
  const { dispatchSetting } = useSettingDispatchChange();
  const enqueueSnackbar = useEnqueueSnackbar();

  const toggleSettingsExpanded = () => {
    setAreSettingsExpanded(!areSettingsExpanded);
  };

  const { control, setValue, watch } = useForm({
    defaultValues: {
      enableticketless: settingValues.enableValetTicketless,
      ticketlesssendmode: String(settingValues.ticketlessSendMode ?? VALET_TICKETLESS_SEND_MODE.MANUAL),
    },
  });

  const isTicketlessEnabled = watch("enableticketless");

  const handleEnableTicketlessChanged = async (e) => {
    const targetValue = e.target.checked;

    if (targetValue === settingValues.enableValetTicketless) return;

    if (
      !(await dispatchSetting(
        entityID,
        "valetarea.enableticketless",
        String(targetValue)
      ))
    ) {
      setValue("enableticketless", settingValues.enableValetTicketless);
      enqueueSnackbar("Unable to change Enable Valet Ticketless setting", {
        variant: "error",
        tag: "EnableTicketlessSettingChangeError",
      });
    }
  };

  const handleTicketlessSendModeChanged = async (e) => {
    const targetValue = e.target.value;

    if (targetValue === String(settingValues.ticketlessSendMode)) return;

    if (
      !(await dispatchSetting(
        entityID,
        "valetarea.ticketlesssendmode",
        targetValue
      ))
    ) {
      setValue("ticketlesssendmode", String(settingValues.ticketlessSendMode));
      enqueueSnackbar("Unable to change Ticketless send mode setting", {
        variant: "error",
        tag: "TicketlessSendModeSettingChangeError",
      });
    }
  };

  return (
    <Grid
      container
      className={classes.panelRoot}
      data-testid="ticketless-settings-container"
    >
      <Accordion
        data-tag="ticketless-settings-accordion"
        expanded={areSettingsExpanded}
        TransitionProps={{
          unmountOnExit: true,
        }}
        className={classes.fullWidthAccordion}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          onClick={toggleSettingsExpanded}
        >
          <Typography className={classes.heading}>Ticketless</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <Controller
                name="enableticketless"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    role="valetarea-enableticketless-toggle"
                    label="Enable Valet Ticketless"
                    control={
                      <Switch
                        {...field}
                        id="valetareaenableticketless"
                        color="primary"
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          handleEnableTicketlessChanged(e);
                        }}
                        checked={field.value}
                        data-checked={field.value}
                      />
                    }
                  />
                )}
              />
            </Grid>
            {isTicketlessEnabled && (
              <Grid item xs={12}>
                <Typography variant="h6">Ticketless send mode</Typography>
                <Controller
                  name="ticketlesssendmode"
                  control={control}
                  render={({ field }) => (
                    <RadioGroup
                      {...field}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        handleTicketlessSendModeChanged(e);
                      }}
                    >
                      <FormControlLabel
                        value={String(VALET_TICKETLESS_SEND_MODE.MANUAL)}
                        control={<Radio color="primary" />}
                        label="Manual Send"
                      />
                      <FormControlLabel
                        value={String(VALET_TICKETLESS_SEND_MODE.AUTO)}
                        control={<Radio color="primary" />}
                        label="Auto Send"
                      />
                    </RadioGroup>
                  )}
                />
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

ValetAreaTicketlessSettingsForm.defaultProps = {
  settingValues: {},
};

export default ValetAreaTicketlessSettingsForm;
